// Class Item Form (Discussion, Assignment, Experiential)
import React, { useState, useEffect } from 'react';

interface ClassItemFormProps {
  onAdd: (item: { title: string; dueDate?: string; notes?: string; metadata?: any }) => void;
  editingItem?: any;
  categoryType?: 'discussion' | 'assignment' | 'experiential' | 'journal';
}

export default function ClassItemForm({ onAdd, editingItem, categoryType = 'discussion' }: ClassItemFormProps) {
  const [title, setTitle] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [notes, setNotes] = useState('');

  // Populate form if editing
  useEffect(() => {
    if (editingItem) {
      setTitle(editingItem.title || '');
      setDueDate(editingItem.dueDate || '');
      setNotes(editingItem.notes || editingItem.description || '');
    }
  }, [editingItem]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!title.trim()) {
      alert('Please enter a title!');
      return;
    }

    const emoji = {
      discussion: '💬',
      assignment: '📝',
      experiential: '🧪',
      journal: '📓'
    };

    onAdd({
      title: `${emoji[categoryType]} ${title.trim()}`,
      dueDate: dueDate || undefined,
      notes: notes || undefined,
      metadata: {
        type: categoryType,
        category: categoryType
      }
    });

    // Reset form
    setTitle('');
    setDueDate('');
    setNotes('');
  };

  const categoryLabel = {
    discussion: 'Discussion',
    assignment: 'Assignment',
    experiential: 'Experiential',
    journal: 'Journal'
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Title */}
      <div>
        <label className="block text-white text-xl font-semibold mb-3">
          {categoryLabel[categoryType]} Title
        </label>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder={`Enter ${categoryLabel[categoryType].toLowerCase()} title...`}
          className="w-full px-6 py-4 bg-white/10 border-2 border-white/20 rounded-2xl text-white text-xl placeholder-white/40 focus:border-violet-400 focus:outline-none transition-colors"
          autoFocus
        />
      </div>

      {/* Due Date (optional) */}
      <div>
        <label className="block text-white text-xl font-semibold mb-3">
          Due Date (Optional)
        </label>
        <input
          type="datetime-local"
          value={dueDate}
          onChange={(e) => setDueDate(e.target.value)}
          className="w-full px-6 py-4 bg-white/10 border-2 border-white/20 rounded-2xl text-white text-xl focus:border-violet-400 focus:outline-none transition-colors"
        />
      </div>

      {/* Notes */}
      <div>
        <label className="block text-white text-xl font-semibold mb-3">
          Notes / Description (Optional)
        </label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Add any notes or details..."
          rows={6}
          className="w-full px-6 py-4 bg-white/10 border-2 border-white/20 rounded-2xl text-white text-xl placeholder-white/40 focus:border-violet-400 focus:outline-none transition-colors resize-none"
        />
      </div>

      {/* Submit Button */}
      <button
        type="submit"
        className="w-full py-6 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white text-2xl font-bold rounded-2xl transition-all active:scale-95 touch-manipulation shadow-2xl"
      >
        {editingItem ? 'Update Item' : `Add ${categoryLabel[categoryType]}`}
      </button>
    </form>
  );
}
